import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatCard, MatCardActions } from '@angular/material/card';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatDatepickerModule, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { take, tap } from 'rxjs';
import { FormInlineErrorComponent } from '@k12/forms';
import { SubTask, Task, TaskTypeAction, WithdrawalReason } from '@k12/models';
import { SchoolsService } from '@k12/school-services';

interface SchoolTransferAndWithdrawalFormData {
  studentName?: string | null;
  studentPid?: string | null;
  termName?: string | null;
  termId?: string | null;
  academicYearId?: number | null;
  reasonForWithdrawal?: number | null;
  firstDateOfAttendance?: Date | null;
  lastDateOfAttendance?: Date | null;
  studentEnrollmentId?: string | null;
}

type TermWindow = {
  schoolTermId: string;
  name: string;
  startDate: Date;
  endDate: Date;
};

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-school-transfer-and-withdrawal-form',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatCard,
    MatButton,
    MatCardActions,
    MatFormField,
    MatSuffix,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    MatDatepickerToggle,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    FormInlineErrorComponent
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './school-transfer-and-withdrawal-form.component.html',
  styleUrl: './school-transfer-and-withdrawal-form.component.scss'
})
export class SchoolTransferAndWithdrawalFormComponent implements OnInit {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);
  private readonly schoolsService = inject(SchoolsService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  @Input() task: Task | null;
  @Input() taskTypeAction: TaskTypeAction | null;
  @Input() taskAction: SubTask | null;
  @Input() isSubmitting = false;
  @Output() submitFormData: EventEmitter<string> = new EventEmitter<string>();

  protected viewMode: 'readonly' | 'edit' = 'edit';
  protected form: FormGroup;

  protected termWindow: TermWindow | null = null;
  protected reasons: WithdrawalReason[] = [];
  protected minAttendanceDate: Date | null = null;
  protected maxAttendanceDate: Date | null = null;
  protected context: SchoolTransferAndWithdrawalFormData = {};
  protected isLoadingStudentInfo = false;

  public ngOnInit(): void {
    this.assertRequiredInputs();
    this.initializeForm();
    this.lockReadOnlyFields();
    this.registerSubscriptions();
    this.restoreFormDataIfPresent();
    this.loadStudentDetails();
    this.getWithdrawalReasons();
    this.applyReadOnlyStatus();
  }

  private assertRequiredInputs(): void {
    if (!this.task || !this.taskTypeAction || !this.taskAction) {
      throw new Error('Task, TaskTypeAction, and TaskAction are required inputs');
    }

    if (!this.taskAction.id) {
      throw new Error('TaskAction must have an id');
    }
  }

  private initializeForm(): void {
    this.form = this.formBuilder.group(
      {
        studentName: null,
        studentPid: null,
        termName: null,
        termId: [null],
        reasonForWithdrawal: [null, Validators.required],
        firstDateOfAttendance: [null],
        lastDateOfAttendance: [null],
        studentEnrollmentId: [null]
      },
      {
        validators: [(group) => this.validateAttendanceDateRange(group as FormGroup)]
      }
    );
  }

  private lockReadOnlyFields(): void {
    this.form.get('studentName')?.disable();
    this.form.get('studentPid')?.disable();
    this.form.get('termName')?.disable();
    this.form.get('termId')?.disable();
  }

  private registerSubscriptions(): void {
    this.form
      .get('reasonForWithdrawal')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reasonId) => {
        this.applyReasonRules(this.coerceReasonId(reasonId));
      });

    this.form
      .get('firstDateOfAttendance')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Revalidate last date when first date changes
        this.form.get('lastDateOfAttendance')?.updateValueAndValidity({ emitEvent: false });
        this.cdr.markForCheck();
      });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  private loadStudentDetails(): void {
    const studentEnrollmentId = this.form.get('studentEnrollmentId')?.value;
    const academicYearId = this.coerceAcademicYearId(this.context.academicYearId);
    
    if (!studentEnrollmentId) return;

    this.isLoadingStudentInfo = true;
    this.cdr.markForCheck();

    this.schoolsService
      .getStudentRoster(studentEnrollmentId, academicYearId)
      .pipe(
        take(1),
        tap((response) => {
          if (response?.entity) {
            // Filter by studentEnrollmentId if provided, otherwise use first item
            const roster = response.entity;

            if (roster) {
              this.form.patchValue(
                {
                  studentName: `${roster.studentFirstName} ${roster.studentLastName}`,
                  studentPid: roster.pidNumber,
                  termName: roster.termName || 'N/A',
                  termId: roster.termId || null
                },
                { emitEvent: false }
              );

              // Set up term window from roster data if term dates are available
              if (roster.termStartDate && roster.termEndDate) {
                const startDate = new Date(roster.termStartDate);
                const endDate = new Date(roster.termEndDate);
                
                if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime())) {
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);

                  this.termWindow = {
                    schoolTermId: roster.termId || '',
                    name: roster.termName || '',
                    startDate,
                    endDate
                  };
                  
                  this.minAttendanceDate = startDate;
                  this.maxAttendanceDate = endDate;
                }
              }

              this.isLoadingStudentInfo = false;
              this.cdr.markForCheck();
            }
          }
        }),
        tap(() => {
          // Ensure spinner is hidden even if no data is returned
          this.isLoadingStudentInfo = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private restoreFormDataIfPresent(): void {
    const formData = this.taskAction?.formData;
    if (!formData) return;

    const parsed = this.safeParseJson(formData) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return;

    this.context = this.normalizeKeys(parsed) as SchoolTransferAndWithdrawalFormData;
    this.form.patchValue(this.context, { emitEvent: false });

    // Back-compat: older payloads may store the full object.
    const restored = this.form.get('reasonForWithdrawal')?.value as unknown;
    const restoredReasonId = this.normalizeWithdrawalReasonId(restored);

    if (restoredReasonId) {
      // Keep the restored value as-is since it may be the full object or just the ID
      // The normalizeWithdrawalReasonId handles both cases
    }

    this.applyReasonRules(restoredReasonId, false);
  }
  
  private normalizeKeys(raw: Record<string, unknown>): Record<string, unknown> {
    return Object.fromEntries(Object.entries(raw).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v]));
  }

  private applyReadOnlyStatus(): void {
    const statusId = this.taskAction?.taskStatusId;
    if (statusId !== 1 && statusId !== 4) {
      this.form.disable();
    }
  }

  private safeParseJson(value: string): unknown {
    try {
      return JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }

  private normalizeWithdrawalReasonId(value: unknown): number | null {
    if (typeof value === 'number') {
      return value;
    }

    if (value && typeof value === 'object' && 'withdrawalReasonId' in value) {
      const id = (value as { withdrawalReasonId?: unknown }).withdrawalReasonId;
      if (typeof id === 'number') {
        return id;
      }
    }

    return null;
  }

  protected submitForm(): void {
    this.submitFormData.emit(JSON.stringify(this.form.getRawValue()));
  }

  protected isFormReady(): boolean {
    return this.form.valid && this.form.dirty;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }

  protected requiresAttendanceDates(): boolean {
    const reasonId = this.coerceReasonId(this.form.get('reasonForWithdrawal')?.value);
    // Withdrawal = 2, Expulsion = 3
    return reasonId === 2 || reasonId === 3;
  }

  private coerceReasonId(value: unknown): number | null {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private coerceAcademicYearId(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isNaN(value) ? null : value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  private applyReasonRules(reasonId: number | null, emitEvent = true): void {
    const first = this.form.get('firstDateOfAttendance');
    const last = this.form.get('lastDateOfAttendance');

    // Withdrawal = 2, Expulsion = 3
    if (reasonId === 2 || reasonId === 3) {
      first?.setValidators([Validators.required, this.dateRangeValidator.bind(this)]);
      last?.setValidators([
        Validators.required,
        this.dateRangeValidator.bind(this),
        this.lastDateAfterFirstValidator.bind(this)
      ]);
    } else {
      first?.clearValidators();
      last?.clearValidators();

      this.form.patchValue(
        { firstDateOfAttendance: null, lastDateOfAttendance: null },
        { emitEvent }
      );
    }

    first?.updateValueAndValidity({ emitEvent: false });
    last?.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    // If no term window (no student/program partner), skip range validation
    if (!control.value || !this.termWindow) {
      return null;
    }

    const selectedDate = new Date(control.value);
    selectedDate.setHours(0, 0, 0, 0);
    const min = this.termWindow.startDate.getTime();
    const max = this.termWindow.endDate.getTime();
    const selected = selectedDate.getTime();

    if (selected < min || selected > max) {
      const dateRange = this.formatDateRange(this.termWindow.startDate, this.termWindow.endDate);
      return {
        dateOutOfRange: {
          message: `Date must be within term range: ${dateRange}`,
          min: this.minAttendanceDate,
          max: this.maxAttendanceDate,
          dateRange
        }
      };
    }

    return null;
  }

  private lastDateAfterFirstValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const firstDateControl = this.form?.get('firstDateOfAttendance');
    const firstDate = firstDateControl?.value;

    if (!firstDate) {
      return null;
    }

    const first = new Date(firstDate);
    const last = new Date(control.value);
    first.setHours(0, 0, 0, 0);
    last.setHours(0, 0, 0, 0);

    if (last.getTime() < first.getTime()) {
      return {
        dateOrder: {
          message: 'Last date of attendance must be on or after the first date of attendance'
        }
      };
    }

    return null;
  }

  private getWithdrawalReasons(): void {
    this.schoolsService
      .getWithdrawalReasonsForSchool()
      .pipe(
        take(1),
        tap((resp) => {
          this.reasons = resp?.entity ?? [];
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  private formatDateRange(startDate: Date, endDate: Date): string {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const start = startDate.toLocaleDateString('en-US', options);
    const end = endDate.toLocaleDateString('en-US', options);
    return `${start} - ${end}`;
  }

  private validateAttendanceDateRange(group: FormGroup): ValidationErrors | null {
    const reasonId = this.coerceReasonId(group.get('reasonForWithdrawal')?.value);
    // Withdrawal = 2, Expulsion = 3
    const requiresDates = reasonId === 2 || reasonId === 3;

    if (!requiresDates) {
      return null;
    }

    const firstDate = group.get('firstDateOfAttendance')?.value;
    const lastDate = group.get('lastDateOfAttendance')?.value;

    if (!firstDate || !lastDate) {
      return {
        attendanceDateRange: {
          message: 'Both first and last date of attendance are required for Withdrawal or Expulsion.'
        }
      };
    }

    // If no term window exists (no student/program partner), skip term validation
    // Dates are unbounded in this case
    return null;
  }
}
